import { useEffect, useRef, useState, createContext, useContext } from 'react';
import { Outlet, useLocation, Link } from 'react-router-dom';
import { Menu } from 'lucide-react';
import { AppSidebar } from '@/features/dashboard/AppSidebar';

// Sidebar context — consumed by Calculator.tsx and DashboardHeader
const SidebarContext = createContext<{
  openSidebar: () => void;
  isCollapsed: boolean;
  toggleCollapse: () => void;
}>({ openSidebar: () => {}, isCollapsed: false, toggleCollapse: () => {} });

// eslint-disable-next-line react-refresh/only-export-components
export const useSidebar = () => useContext(SidebarContext);
import { courseModules, taxCourseModules } from '@/features/content-library/courseData';
import { useUserRole } from '@/hooks/useUserRole';
import { useMemberProgress } from '@/features/dashboard/useMemberProgress';
import { WeeklyOneProvider } from '@/features/weekly-one/WeeklyOneContext';
import WeeklyOneSetModal from '@/features/weekly-one/WeeklyOneSetModal';
import WeeklyOneDoneModal from '@/features/weekly-one/WeeklyOneDoneModal';
import WeeklyOneReviewModal from '@/features/weekly-one/WeeklyOneReviewModal';

const COURSE_CONFIG: Record<string, { title: string; coursePath: string; modules: typeof courseModules }> = {
  course: { title: 'Real Estate Investing Made Easy', coursePath: '/content/course/course', modules: courseModules },
  'tax-savings': { title: 'Real Estate Tax Savings Blueprint', coursePath: '/content/course/tax-savings', modules: taxCourseModules },
};

// Each crumb: label + optional URL (omit URL for the current/last page)
const BREADCRUMB_MAP: Record<string, { label: string; url?: string }[]> = {
  '/dashboard':              [{ label: 'Dashboard' }],
  '/mentors':                [{ label: 'Member Hub', url: '/dashboard' }, { label: 'Mentors' }],
  '/calendar':               [{ label: 'Member Hub', url: '/dashboard' }, { label: 'Calendar' }],
  '/content':                [{ label: 'Content Library' }],
  '/content/fast-track':     [{ label: 'Content Library', url: '/content' }, { label: 'Fast Track Recordings' }],
  '/calculators':            [{ label: 'Calculators' }],
  '/dashboard/tools/rental-analyzer':     [{ label: 'Calculators', url: '/calculators' }, { label: 'Rental Property Analyzer' }],
  '/dashboard/tools/str-calculator':      [{ label: 'Calculators', url: '/calculators' }, { label: 'Short-Term Rental Analyzer' }],
  '/dashboard/tools/mortgage-calculator': [{ label: 'Calculators', url: '/calculators' }, { label: 'Mortgage Calculator' }],
  '/dashboard/tools/brrrr-calculator':    [{ label: 'Calculators', url: '/calculators' }, { label: 'BRRRR Calculator' }],
  '/dashboard/tools/roe-calculator':      [{ label: 'Calculators', url: '/calculators' }, { label: 'Return on Equity Calculator' }],
  '/resources':              [{ label: 'Resources' }],
  '/profile':                [{ label: 'Profile' }],
  '/habits':                 [{ label: 'Goals & Habits' }],
  '/saved-deals':            [{ label: 'Profile', url: '/profile' }, { label: 'Saved Analysis & Tools' }],
  '/financial-freedom-declaration': [{ label: 'Profile', url: '/profile' }, { label: 'Financial Freedom Declaration' }],
  '/cashflow-calculator':         [{ label: 'Calculators', url: '/calculators' }, { label: 'Cash Flow Calculator' }],
  '/dashboard/cashflow-calculator': [{ label: 'Calculators', url: '/calculators' }, { label: 'Cash Flow Calculator' }],
  '/strategy-finder':        [{ label: 'Calculators', url: '/calculators' }, { label: 'Strategy Finder' }],
  '/admin':                  [{ label: 'Admin' }],
  '/admin/members':          [{ label: 'Admin', url: '/admin' }, { label: 'Members' }],
  '/admin/fast-track':       [{ label: 'Admin', url: '/admin' }, { label: 'Fast Track Cohorts' }],
  '/admin/kpi':              [{ label: 'Admin', url: '/admin' }, { label: 'Weekly KPI Activity' }],
  '/admin/bugs':             [{ label: 'Admin', url: '/admin' }, { label: 'Bug Reports' }],
  '/admin/tool-suggestions': [{ label: 'Admin', url: '/admin' }, { label: 'Tool Suggestions' }],
  '/admin/analytics':        [{ label: 'Admin', url: '/admin' }, { label: 'Analytics' }],
  '/admin/feedback':         [{ label: 'Admin', url: '/admin' }, { label: 'Fast Track Feedback' }],
  '/admin/settings':         [{ label: 'Admin', url: '/admin' }, { label: 'Settings' }],
  '/admin/ref-leads':        [{ label: 'Admin', url: '/admin' }, { label: 'BetterLife REF Leads' }],
  '/strategy-finder-admin':  [{ label: 'Admin', url: '/admin' }, { label: 'Strategy Finder Settings' }],
  '/content/course':         [{ label: 'Content Library', url: '/content' }, { label: 'Course' }],
  '/marketfinder':           [{ label: 'Tools', url: '/tools' }, { label: 'Market Finder' }],
  '/action-tracker':         [{ label: 'Member Hub', url: '/dashboard' }, { label: 'Action Tracker' }],
  '/admin/blockers':         [{ label: 'Admin', url: '/admin' }, { label: 'Member Blockers' }],
  '/roadmap':                [{ label: 'Dashboard', url: '/dashboard' }, { label: 'First Deal Journey' }],
};

function buildContentBreadcrumbs(pathname: string): { label: string; url?: string }[] | null {
  // Match /content/course/:slug, /content/course/:slug/:moduleId, /content/course/:slug/:moduleId/:lessonId
  const match = pathname.match(/^\/content\/course\/([^/]+)(?:\/([^/]+))?(?:\/([^/]+))?$/);
  if (!match) return null;

  const [, slug, moduleId, lessonId] = match;
  const config = COURSE_CONFIG[slug];
  if (!config) return null;

  const crumbs: { label: string; url?: string }[] = [
    { label: 'Content Library', url: '/content' },
    { label: config.title, url: moduleId ? config.coursePath : undefined },
  ];

  if (moduleId) {
    const mod = config.modules.find((m) => m.id === moduleId);
    if (mod) {
      const modLabel = `Module ${mod.moduleNumber}`;
      crumbs.push({ label: modLabel, url: lessonId ? `${config.coursePath}/${moduleId}` : undefined });
    }
    if (lessonId) {
      const mod2 = config.modules.find((m) => m.id === moduleId);
      const lesson = mod2?.lessons.find((l) => l.id === lessonId);
      if (lesson) {
        crumbs.push({ label: `Lesson ${lesson.lessonNumber}: ${lesson.title}` });
      }
    }
  }

  return crumbs;
}

function DashboardHeader() {
  const { pathname } = useLocation();
  const { openSidebar } = useSidebar();

  const crumbs =
    BREADCRUMB_MAP[pathname] ??
    buildContentBreadcrumbs(pathname) ?? [
      { label: pathname.split('/').filter(Boolean).pop()?.replace(/-/g, ' ') ?? 'Page' },
    ];

  return (
    <header className="flex-shrink-0 border-b border-border bg-background flex items-center justify-between px-4 sm:px-6 gap-4" style={{ paddingTop: 'env(safe-area-inset-top)', minHeight: 'calc(3.5rem + env(safe-area-inset-top))' }}>
      {/* Mobile hamburger */}
      <button
        onClick={openSidebar}
        className="lg:hidden p-2 -ml-1 text-muted-foreground hover:text-foreground transition-colors rounded-lg flex-shrink-0"
        aria-label="Open navigation"
      >
        <Menu className="w-5 h-5" />
      </button>
      {/* Breadcrumb */}
      <nav className="flex items-center gap-1.5 text-xs uppercase tracking-widest font-medium text-muted-foreground flex-shrink-0 overflow-hidden">
        {crumbs.map((crumb, i) => (
          <span key={i} className="flex items-center gap-1.5 min-w-0">
            {i > 0 && <span className="text-muted-foreground/40 flex-shrink-0">&rsaquo;</span>}
            {crumb.url ? (
              <Link to={crumb.url} className="hover:text-foreground transition-colors truncate">
                {crumb.label}
              </Link>
            ) : (
              <span className={`truncate ${i === crumbs.length - 1 ? 'text-foreground' : ''}`}>
                {crumb.label}
              </span>
            )}
          </span>
        ))}
      </nav>

    </header>
  );
}

const COLLAPSE_KEY = 'sidebar_collapsed';

function DashboardLayoutInner() {
  const { pathname } = useLocation();
  const { isMember, isAdmin, loading: roleLoading } = useUserRole();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem(COLLAPSE_KEY) === 'true'; } catch { return false; }
  });
  const mainRef = useRef<HTMLElement>(null);
  useMemberProgress();

  const toggleCollapse = () => {
    setSidebarCollapsed(prev => {
      const next = !prev;
      try { localStorage.setItem(COLLAPSE_KEY, String(next)); } catch { /* ignore quota / private mode */ }
      return next;
    });
  };

  // Close sidebar and scroll to top on route change
  useEffect(() => {
    setSidebarOpen(false);
    if (mainRef.current) mainRef.current.scrollTop = 0;
  }, [pathname]);

  // Lock body scroll when sidebar drawer is open on mobile
  useEffect(() => {
    if (sidebarOpen) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [sidebarOpen]);

  // Routes that manage their own full-screen layout (no header, no padding)
  const FULL_BLEED_PATHS = new Set(['/dashboard/tools/rental-analyzer', '/dashboard/tools/str-calculator']);
  const isFullBleed = FULL_BLEED_PATHS.has(pathname);

  return (
    <SidebarContext.Provider value={{ openSidebar: () => setSidebarOpen(true), isCollapsed: sidebarCollapsed, toggleCollapse }}>
      <div className="h-screen flex w-full overflow-hidden">
        {/* Mobile backdrop */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}
        <AppSidebar
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          isCollapsed={sidebarCollapsed}
          onToggleCollapse={toggleCollapse}
        />
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
          {!isFullBleed && <DashboardHeader />}
          <main ref={mainRef} className={`flex-1 ${isFullBleed ? 'overflow-hidden h-full flex flex-col' : 'px-4 py-4 sm:px-10 sm:py-8 overflow-y-auto overflow-x-hidden overscroll-y-none'}`}>
            <Outlet />
          </main>
        </div>
        {isMember && <WeeklyOneSetModal />}
        {isMember && <WeeklyOneDoneModal />}
        {isMember && <WeeklyOneReviewModal />}
      </div>
    </SidebarContext.Provider>
  );
}

const DashboardLayout = () => (
  <WeeklyOneProvider>
    <DashboardLayoutInner />
  </WeeklyOneProvider>
);

export default DashboardLayout;
